import type React from 'react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getLiveUrl, getAdminUrl } from '@/utils/crossAppLinks';
import { useUser } from '@showliner/shared/context/UserContext';
import Logo from '@showliner/shared/components/Logo';

const navLinks = [
  { label: 'Venues', href: '/for-venues' },
  { label: 'Artists', href: '/for-performers' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Discover', href: '/discover' },
];

const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user } = useUser();

  return (
    <header className="sticky top-0 z-50 w-full border-b border-grey-200 bg-grey-50">
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4 sm:px-8">
        <Link to="/" className="flex items-center gap-2 -ml-1" aria-label="Showliner home">
          <Logo variant="full" theme="light" className="h-6 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-7" aria-label="Primary">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={cn(
                'text-sm transition-colors',
                location.pathname === link.href
                  ? 'text-ink font-medium'
                  : 'text-grey-700 hover:text-ink'
              )}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-5">
          {user ? (
            <>
              <a
                href={getAdminUrl('/dashboard')}
                className="text-sm text-grey-700 hover:text-ink transition-colors"
              >
                Dashboard
              </a>
              <a
                href={getLiveUrl()}
                className="text-sm text-grey-700 hover:text-ink transition-colors"
              >
                Live
              </a>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="text-sm text-grey-700 hover:text-ink transition-colors"
              >
                Sign in
              </Link>
              <Button size="sm" asChild>
                <a href={getAdminUrl('/register')}>Request access</a>
              </Button>
            </>
          )}
        </div>

        <button
          type="button"
          className="md:hidden p-2 -mr-2 text-grey-700 hover:text-ink"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden border-t border-grey-200 bg-grey-50">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  'block px-2 py-2 text-base',
                  location.pathname === link.href
                    ? 'text-ink font-medium'
                    : 'text-grey-700'
                )}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-3 mt-3 border-t border-grey-200 space-y-1">
              {user ? (
                <>
                  <a
                    href={getAdminUrl('/dashboard')}
                    className="block px-2 py-2 text-base text-grey-700"
                  >
                    Dashboard
                  </a>
                  <a
                    href={getLiveUrl()}
                    className="block px-2 py-2 text-base text-grey-700"
                  >
                    Live rooms
                  </a>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-2 py-2 text-base text-grey-700"
                  >
                    Sign in
                  </Link>
                  <a
                    href={getAdminUrl('/register')}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-2 py-2 text-base font-medium text-primary-600"
                  >
                    Request access →
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
